import { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import formatDateForRequest from '../utilities/formatDateForRequest';
import getUrlPrefix from '../utilities/getUrlPrefix';
import axios from '../axios';
import { notify } from '../Notification';
import constants from '../constants';
import {
  CoverageDataResponse,
  EditEnrollmentsPayload,
  EmployeeDetailsData,
  PlanYear,
} from './types';
import { Dayjs } from 'dayjs';

export type UpdateCoverageArgs = {
  plan: any;
  coverageId: string;
  coverageStartDate: Dayjs;
  coverageType?: string;
};

interface UseEmployeeActionsProps {
  employeeId: number | undefined;
  employerId: number | null;
  refresh: () => Promise<void>;
  portal: string;
  isEmployee?: boolean;
}

type Payload = {
  patient: string;
  offer_benefits: boolean;
};

export default function useEmployeeActions({
  employeeId,
  employerId,
  refresh,
  portal,
  isEmployee,
}: UseEmployeeActionsProps) {
  const [employee, setEmployee] = useState<EmployeeDetailsData>();
  const [coverage, setCoverage] = useState<CoverageDataResponse | null>(null);
  const [selectedPlanYearId, setSelectedPlanYearId] = useState('');
  const [planYears, setPlanYears] = useState<PlanYear[]>([]);
  const [showLoader, setShowLoader] = useState(false);

  const queryClient = useQueryClient();

  const urlPrefix = useMemo(() => getUrlPrefix(portal), [portal]);

  const getPlans = useCallback(
    (planYear: PlanYear) => {
      if (portal !== constants.PORTALS.EMPLOYER && !employerId) return Promise.resolve();

      return axios
        .get(`${urlPrefix}/plan/`, {
          params: {
            ...(planYear.id && { plan_year: planYear.id }),
            ...(portal !== constants.PORTALS.EMPLOYER && { corporation_pk: employerId }),
            include_vision: true,
          },
        })
        .then((response) => {
          return response.data.data;
        })
        .catch((error: Error) => {
          notify(error.message, 'error');
        });
    },
    [employerId, portal, urlPrefix]
  );

  const getPlanYears = useCallback(() => {
    if (portal !== constants.PORTALS.EMPLOYER && !employerId) return Promise.resolve();
    const corporationPk = portal !== constants.PORTALS.EMPLOYER ? employerId : undefined;

    return axios
      .get(`${urlPrefix}/plan-year/`, {
        params: {
          ...(corporationPk && { corporation_pk: corporationPk }),
          ...(employeeId && { employee: employeeId }),
        },
      })
      .then((response) => {
        const newPlanYears = response.data.data;
        const currentPlanYear =
          newPlanYears.find((py: PlanYear) => py.is_current) || newPlanYears[0];
        setPlanYears(newPlanYears);
        setSelectedPlanYearId(currentPlanYear?.id || null);
      })
      .catch((error: Error) => {
        notify(error.message, 'error');
      });
  }, [employerId, employeeId, portal, urlPrefix]);

  const getEmployee = useCallback(
    (id: number) => {
      if (portal !== constants.PORTALS.EMPLOYER && !employerId) return Promise.resolve();

      setShowLoader(true);
      // eslint-disable-next-line consistent-return
      return axios
        .get(`${urlPrefix}/employee/${id}/`, {
          params: {
            ...(portal !== constants.PORTALS.EMPLOYER && employerId
              ? { corporation_pk: employerId.toString() }
              : {}),
          },
        })
        .then((response) => {
          setEmployee(response.data.data);
          setShowLoader(false);
        })
        .catch((error: Error) => {
          notify(error.message, 'error');
        });
    },
    [employerId, portal, urlPrefix]
  );

  const getCoverage = useCallback(
    (employeeId: number, planYearId: string | null) => {
      if (!employeeId) return;
      if (portal !== constants.PORTALS.EMPLOYER && !employerId) return Promise.resolve();

      setShowLoader(true);
      // eslint-disable-next-line consistent-return
      return axios
        .get(`${urlPrefix}/employee/${employeeId}/coverage-data/`, {
          params: {
            ...(portal !== constants.PORTALS.EMPLOYER && employerId
              ? { corporation_pk: employerId.toString() }
              : {}),
            plan_year: planYearId?.toString() || '',
          },
        })
        .then((response: { data: CoverageDataResponse }) => {
          setCoverage(response.data);
          setShowLoader(false);
        })
        .catch((error: Error) => {
          notify(error.message, 'error');
        });
    },
    [employerId, portal, urlPrefix]
  );

  const savePatient = (data: Partial<EmployeeDetailsData>): Promise<void> => {
    const api = portal === constants.PORTALS.ADMIN ? 'member' : 'patients';
    return axios.patch(`${urlPrefix}/${api}/${(employee as EmployeeDetailsData).patient}/`, data);
  };

  const saveEmployee = (payload: Payload) => {
    let url = `${urlPrefix}/employee/${(employee as EmployeeDetailsData).id}/`;
    if (portal !== constants.PORTALS.EMPLOYER) {
      url += `?corporation_pk=${employerId}`;
    }
    return axios.put(url, payload);
  };

  const refreshCoverage = () => {
    if (!employeeId) {
      return;
    }
    const result = getCoverage(employeeId, selectedPlanYearId);
    if (result && typeof result.then === 'function') {
      result.then(refresh);
    }
  };

  const refreshEmployee = () => {
    if (!employeeId) {
      return;
    }
    getEmployee(employeeId).then(refresh);
  };

  const bulkEnrollEmployee = (payload: EditEnrollmentsPayload): Promise<void> => {
    const params = new URLSearchParams();
    if (portal !== constants.PORTALS.EMPLOYER && employerId) {
      params.append('corporation_pk', employerId.toString());
    }

    const url = `${urlPrefix}/employee/${employeeId}/bulk-enroll/?${params.toString()}`;
    setShowLoader(true);
    return axios
      .post(url, payload)
      .then(() => notify('Enrollment changes saved', 'success'))
      .then(refreshCoverage)
      .catch((error) => {
        throw error;
      })
      .finally(() => setShowLoader(false));
  };

  const updateCoverageMutation = useMutation(
    ({ plan, coverageId, coverageStartDate, coverageType }: UpdateCoverageArgs) => {
      const params = new URLSearchParams();
      if (portal !== constants.PORTALS.EMPLOYER && employerId) {
        params.append('corporation_pk', employerId.toString());
      }
      const url = `${urlPrefix}/employee/${employeeId}/update-coverage/?${params.toString()}`;

      return axios
        .post(url, {
          plan_year_id: selectedPlanYearId,
          plan_type: plan.type,
          plan_id: plan.id,
          coverage_start_date: formatDateForRequest(coverageStartDate),
          coverage_id: coverageId,
          coverage_type: coverageType,
        })
        .catch((error: Error) => notify(error.message, 'error'));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['employees', employeeId, 'coverage', selectedPlanYearId]);
      },
    }
  );

  const updateCoverageHandler = (args: UpdateCoverageArgs) =>
    updateCoverageMutation.mutateAsync(args).then(() => undefined);

  useEffect(() => {
    if (!employeeId) {
      return;
    }

    if (portal !== constants.PORTALS.EMPLOYER && !employerId) {
      return;
    }

    getEmployee(employeeId);
  }, [employeeId, employerId, getEmployee, portal]);

  useEffect(() => {
    if (!employeeId) {
      return;
    }

    setEmployee(undefined);
    setCoverage(null);
    setSelectedPlanYearId('');
  }, [employeeId]);

  useEffect(() => {
    getPlanYears();
  }, [getPlanYears]);

  useEffect(() => {
    if (portal !== constants.PORTALS.EMPLOYER && !employerId) {
      return;
    }

    if (
      employee &&
      selectedPlanYearId &&
      String((employee as EmployeeDetailsData).id) === String(employeeId)
    ) {
      getCoverage((employee as EmployeeDetailsData).id, selectedPlanYearId);
    }
  }, [employee, employeeId, employerId, getCoverage, portal, selectedPlanYearId]);

  const fullResult = {
    showLoader,
    employee,
    planYears,
    selectedPlanYearId,
    setSelectedPlanYearId,
    coverage,
    refreshCoverage,
    refreshEmployee,
    updateCoverage: updateCoverageHandler,
    bulkEnrollEmployee,
    savePatient,
    saveEmployee,
    getPlans,
  };

  const nullActions = {
    refreshCoverage: null,
    refreshEmployee: null,
    updateCoverage: null,
    bulkEnrollEmployee: null,
    savePatient: null,
    saveEmployee: null,
    getPlans: null,
  };

  return isEmployee ? fullResult : { ...fullResult, ...nullActions };
}
